import { FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import { getPerPlatformRateRows } from "../../utils/campaignRates";

/** Creator Rewards' own colours on ds buttons, as the Campaign frames use them. */
export const APP_FILLED = "!bg-app-creator-rewards-base !text-app-creator-rewards-on";
export const APP_TINTED = "!bg-app-creator-rewards-deep !text-app-creator-rewards-soft";

/** The app's colours on the selected view, as the Campaign frames draw it. */
export const SELECTED_TAB =
  "bg-app-creator-rewards-deep text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--app-creator-rewards-soft)]";

/** The four places a creator posts: which campaign flags accept them, where their links live. */
export const PLATFORMS = [
  { id: "tiktok", name: "TikTok", Logo: FaTiktok, color: "text-label-primary", rateKeys: ["tiktok"], format: "TikTok", hosts: ["tiktok.com"] },
  { id: "instagram", name: "Instagram", Logo: FaInstagram, color: "text-[#e4405f]", rateKeys: ["instagramReels"], format: "Reels", hosts: ["instagram.com", "instagr.am"] },
  { id: "facebook", name: "Facebook", Logo: FaFacebookF, color: "text-[#1877f2]", rateKeys: ["facebookReels"], format: "Reels", hosts: ["facebook.com", "fb.com", "fb.watch"] },
  { id: "youtube", name: "YouTube", Logo: FaYoutube, color: "text-[#ff0000]", rateKeys: ["youtubeShorts", "youtubeLongForm"], format: "Shorts", hosts: ["youtube.com", "youtu.be"] },
];

export const platformById = (id) => PLATFORMS.find((platform) => platform.id === id);

export const platformForRateKey = (key) => PLATFORMS.find((platform) => platform.rateKeys.includes(key));

export const acceptedPlatforms = (campaign) =>
  PLATFORMS.filter((platform) => platform.rateKeys.some((key) => campaign?.platforms?.[key]));

/** The platform a pasted link belongs to, or null when the address isn't one we know. */
export const platformFromLink = (link) => {
  const text = String(link || "").trim();
  if (!text) return null;
  try {
    const { hostname } = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    const host = hostname.toLowerCase();
    return PLATFORMS.find((platform) => platform.hosts.some((h) => host === h || host.endsWith(`.${h}`))) || null;
  } catch {
    return null;
  }
};

/** What this campaign pays on a platform, per `viewsPerRate` views. */
export const platformRate = (campaign, platform) => {
  const rows = getPerPlatformRateRows(campaign);
  if (rows[0]?.legacy) return rows[0].rate;
  return rows.find((row) => platform.rateKeys.includes(row.key))?.rate ?? null;
};

/** "1 ₾ / 1K" */
export const shortRate = (rate, viewsPerRate = 1000) => {
  const views = viewsPerRate >= 1000 ? `${viewsPerRate / 1000}K` : viewsPerRate;
  return `${rate} ₾ / ${views}`;
};

export const PlatformLogo = ({ platform, size = 18, className = "" }) => {
  const entry = typeof platform === "string" ? platformById(platform) : platform;
  if (!entry) return null;
  const { Logo, color } = entry;
  return <Logo size={size} className={`shrink-0 ${color} ${className}`} aria-hidden="true" />;
};

/** The 36px rounded tile a platform logo sits on in lists. */
export const PlatformTile = ({ platform }) => (
  <span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-fill-tertiary">
    <PlatformLogo platform={platform} />
  </span>
);

/** Figma "Chip": h36, selected in the app's colours. */
export const FilterChip = ({ selected = false, onClick, children }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={`flex h-9 shrink-0 items-center gap-[6px] rounded-full px-space-16 text-footnote-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
      selected
        ? "bg-app-creator-rewards-deep text-app-creator-rewards-soft shadow-[inset_0_0_0_1px_var(--app-creator-rewards-base)]"
        : "bg-fill-secondary text-label-secondary"
    }`}
  >
    {children}
  </button>
);

/** A link without its protocol or "www.", for lists. */
export const shortLink = (link) => String(link || "").replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

/** "@name" out of a profile link. */
export const handleFromLink = (link) => {
  const text = String(link || "").trim();
  try {
    const { pathname } = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    const last = pathname.split("/").filter(Boolean).pop();
    if (!last) return text;
    return last.startsWith("@") ? last : `@${last}`;
  } catch {
    return text;
  }
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 24 · 18:02", with the app's own month names. */
export const formatWhen = (t, value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  return `${t(`common.month${MONTHS[date.getMonth()]}`)} ${date.getDate()} · ${time}`;
};
